import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { FanUser } from '@tsuyosa/types';

export interface AuthError extends Error {
  code?: string;
}

export interface RegisterPayload {
  email: string;
  password: string;
  username: string;
  display_name?: string;
}

interface AuthContextValue {
  user: FanUser | null;
  accessToken: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: () => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const REFRESH_MS = 50 * 60 * 1000;

export function AuthProvider({ apiUrl, children }: { apiUrl: string; children: React.ReactNode }) {
  const [user, setUser]               = useState<FanUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading]     = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function clearTimer() {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }

  function scheduleRefresh() {
    clearTimer();
    timerRef.current = setTimeout(silentRefresh, REFRESH_MS);
  }

  function handleLogoutCleanup() {
    clearTimer();
    setUser(null);
    setAccessToken(null);
  }

  async function silentRefresh() {
    try {
      const res = await fetch(`${apiUrl}/api/v1/fan/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) { handleLogoutCleanup(); return; }
      const data = await res.json() as { data: { access_token: string } };
      const profileRes = await fetch(`${apiUrl}/api/v1/me/profile`, {
        headers: { Authorization: `Bearer ${data.data.access_token}` },
      });
      if (profileRes.ok) {
        const profileData = await profileRes.json() as { data: FanUser };
        setUser(profileData.data);
      }
      setAccessToken(data.data.access_token);
      scheduleRefresh();
    } catch {
      handleLogoutCleanup();
    }
  }

  useEffect(() => {
    silentRefresh().finally(() => setIsLoading(false));
    return () => clearTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function login(email: string, password: string) {
    const res = await fetch(`${apiUrl}/api/v1/fan/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json() as {
      success: boolean;
      data: { access_token: string; user: FanUser };
      error?: { code: string; message: string };
    };
    if (res.status === 403 && data.error?.code === 'EMAIL_NOT_VERIFIED') {
      const err: AuthError = new Error(data.error.message ?? 'Email belum diverifikasi');
      err.code = 'EMAIL_NOT_VERIFIED';
      throw err;
    }
    if (!res.ok || !data.success) throw new Error(data.error?.message ?? 'Login gagal');
    setUser(data.data.user);
    setAccessToken(data.data.access_token);
    scheduleRefresh();
  }

  async function register(payload: RegisterPayload) {
    const res = await fetch(`${apiUrl}/api/v1/fan/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json() as { success: boolean; error?: { message: string } };
    if (!res.ok || !data.success) throw new Error(data.error?.message ?? 'Registrasi gagal');
  }

  async function logout() {
    if (accessToken) {
      await fetch(`${apiUrl}/api/v1/fan/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { Authorization: `Bearer ${accessToken}` },
      }).catch(() => {});
    }
    handleLogoutCleanup();
  }

  async function verifyEmail(token: string) {
    const res = await fetch(`${apiUrl}/api/v1/fan/auth/verify-email`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    const data = await res.json() as {
      success: boolean;
      data: { access_token: string; user: FanUser };
      error?: { message: string };
    };
    if (!res.ok || !data.success) throw new Error(data.error?.message ?? 'Verifikasi gagal');
    setUser(data.data.user);
    setAccessToken(data.data.access_token);
    scheduleRefresh();
  }

  async function resendVerification(email: string) {
    const res = await fetch(`${apiUrl}/api/v1/fan/auth/resend-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await res.json() as { success: boolean; error?: { message: string } };
    if (!res.ok || !data.success) throw new Error(data.error?.message ?? 'Gagal mengirim ulang verifikasi');
  }

  return (
    <AuthContext.Provider value={{ user, accessToken, isLoading, login, register, logout, verifyEmail, resendVerification }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
